/**
 * Global-search results: data and menu sections with keyboard navigation.
 */
import * as React from 'react';
import { Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  SEARCH_COPY,
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_PAGE_SIZE,
  SEARCH_SECTION_LABELS,
} from '@/components/search/search-configs';
import { SearchResultItem } from '@/components/search/SearchResultItem';
import {
  canGoToNextSearchPage,
  canGoToPrevSearchPage,
  formatSearchPageRange,
  nextSearchActiveIndex,
  prevSearchActiveIndex,
} from '@/components/search/search-utils';
import { useGlobalSearchContext } from '@/contexts/GlobalSearchContext';
import type { SearchResultItem as SearchResultItemType } from '@/types/search.types';

/** Props for {@link GlobalSearchDropdown}. */
interface GlobalSearchDropdownProps {
  onClose: () => void;
}

/** Keyboard-navigable dropdown listing data and menu search hits. */
export const GlobalSearchDropdown: React.FC<GlobalSearchDropdownProps> = ({ onClose }) => {
  const {
    isLoadingData,
    hasApiResponded,
    allDataResults,
    allMenuResultsMapped,
    paginatedMenuResults,
    hasDataResults,
    hasMenuResults,
    searchError,
    searchQuery,
    menuPage,
    setMenuPage,
    dataPage,
    setDataPage,
    totalDataCount,
  } = useGlobalSearchContext();

  const [activeIndex, setActiveIndex] = React.useState(-1);

  const allItems = React.useMemo(
    () => [...allDataResults, ...paginatedMenuResults],
    [allDataResults, paginatedMenuResults],
  );

  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => nextSearchActiveIndex(prev, allItems.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => prevSearchActiveIndex(prev));
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0 && activeIndex < allItems.length) {
        e.preventDefault();
        const selectedItem = allItems[activeIndex];
        if (selectedItem) {
          window.location.href = selectedItem.route;
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose, allItems, activeIndex]);

  function handlePrevDataPage(): void {
    setDataPage(dataPage - 1);
  }

  function handleNextDataPage(): void {
    setDataPage(dataPage + 1);
  }

  function handlePrevMenuPage(): void {
    setMenuPage(menuPage - 1);
  }

  function handleNextMenuPage(): void {
    setMenuPage(menuPage + 1);
  }

  function renderDataResult(result: SearchResultItemType, index: number): React.ReactNode {
    return (
      <SearchResultItem
        key={result.id}
        result={result}
        onClick={onClose}
        isActive={index === activeIndex}
      />
    );
  }

  function renderMenuResult(result: SearchResultItemType, index: number): React.ReactNode {
    return (
      <SearchResultItem
        key={result.id}
        result={result}
        onClick={onClose}
        isActive={allDataResults.length + index === activeIndex}
      />
    );
  }

  function renderDataPagination(): React.ReactNode {
    if (totalDataCount <= SEARCH_PAGE_SIZE) return null;
    return (
      <div className="global-search-pagination">
        <span className="global-search-pagination-info">
          {formatSearchPageRange(dataPage, totalDataCount)}
        </span>
        <div className="global-search-pagination-buttons">
          <button
            type="button"
            onClick={handlePrevDataPage}
            disabled={!canGoToPrevSearchPage(dataPage)}
            className="global-search-pagination-button"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={handleNextDataPage}
            disabled={!canGoToNextSearchPage(dataPage, totalDataCount)}
            className="global-search-pagination-button"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  function renderMenuPagination(): React.ReactNode {
    if (allMenuResultsMapped.length <= SEARCH_PAGE_SIZE) return null;
    return (
      <div className="global-search-pagination">
        <span className="global-search-pagination-info">
          {formatSearchPageRange(menuPage, allMenuResultsMapped.length)}
        </span>
        <div className="global-search-pagination-buttons">
          <button
            type="button"
            onClick={handlePrevMenuPage}
            disabled={!canGoToPrevSearchPage(menuPage)}
            className="global-search-pagination-button"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={handleNextMenuPage}
            disabled={!canGoToNextSearchPage(menuPage, allMenuResultsMapped.length)}
            className="global-search-pagination-button"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  function renderDataResults(): React.ReactNode {
    const nodes: React.ReactNode[] = [];
    for (let index = 0; index < allDataResults.length; index++) {
      nodes.push(renderDataResult(allDataResults[index], index));
    }
    return nodes;
  }

  function renderMenuResults(): React.ReactNode {
    const nodes: React.ReactNode[] = [];
    for (let index = 0; index < paginatedMenuResults.length; index++) {
      nodes.push(renderMenuResult(paginatedMenuResults[index], index));
    }
    return nodes;
  }

  function renderDataSection(): React.ReactNode {
    if (!hasDataResults) return null;
    return (
      <div className="global-search-section">
        <div className="global-search-section-header">
          <div className="global-search-section-header-icon">
            <Search className="h-4 w-4" />
          </div>
          <span className="global-search-section-title">{SEARCH_SECTION_LABELS.data}</span>
        </div>
        <div className="global-search-results">{renderDataResults()}</div>
        {renderDataPagination()}
      </div>
    );
  }

  function renderMenuSection(): React.ReactNode {
    if (!hasMenuResults) return null;
    return (
      <div className="global-search-section">
        <div className="global-search-section-header">
          <div className="global-search-section-header-icon">
            <Search className="h-4 w-4" />
          </div>
          <span className="global-search-section-title">{SEARCH_SECTION_LABELS.menus}</span>
        </div>
        <div className="global-search-results">{renderMenuResults()}</div>
        {renderMenuPagination()}
      </div>
    );
  }

  function renderDivider(): React.ReactNode {
    if (!hasDataResults || !hasMenuResults) return null;
    return <div className="global-search-divider" />;
  }

  function renderInlineError(): React.ReactNode {
    if (!searchError) return null;
    return (
      <div className="global-search-empty">
        <p className="global-search-empty-title">{searchError}</p>
      </div>
    );
  }

  if (searchQuery.length < SEARCH_MIN_QUERY_LENGTH) {
    return (
      <div className="global-search-dropdown">
        <div className="global-search-min-length-hint">
          <Search className="h-4 w-4" />
          <span>{SEARCH_COPY.minLength}</span>
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="global-search-dropdown">
        <div className="global-search-loader">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--color-2-nuance-1)' }} />
          <span className="global-search-loader-text">{SEARCH_COPY.loading}</span>
        </div>
      </div>
    );
  }

  if (searchError && !hasMenuResults) {
    return (
      <div className="global-search-dropdown">
        <div className="global-search-empty">
          <Search className="global-search-empty-icon" />
          <p className="global-search-empty-title">{searchError}</p>
        </div>
      </div>
    );
  }

  if (
    searchQuery.length >= SEARCH_MIN_QUERY_LENGTH &&
    hasApiResponded &&
    !hasDataResults &&
    !hasMenuResults
  ) {
    return (
      <div className="global-search-dropdown">
        <div className="global-search-empty">
          <Search className="global-search-empty-icon" />
          <p className="global-search-empty-title">{SEARCH_COPY.emptyTitle}</p>
          <p className="global-search-empty-description">{SEARCH_COPY.emptyDescription}</p>
        </div>
      </div>
    );
  }

  if (!hasApiResponded && searchQuery.length >= SEARCH_MIN_QUERY_LENGTH) {
    return null;
  }

  return (
    <div className="global-search-dropdown">
      <div className="global-search-scrollable scrollbar-thin">
        {renderInlineError()}
        {renderDataSection()}
        {renderDivider()}
        {renderMenuSection()}
      </div>
    </div>
  );
};
